import type { ReviewItem } from "@/api/types";

import type { AppliedAlternativeSelection } from "../LlmSuggestionPanel";

/**
 * Общие утилиты + типы для type-specific форм в recordForms/* и
 * RawPayloadFallback. Раньше дублировались в AdminReviewFocus.tsx и
 * AdminReviewBatch.tsx — вынесли сюда.
 */

// ---------------------------------------------------------------------------
// PayloadShape — все поля review_items.payload, которые встречаются в живых
// данных. Каждая type-specific форма читает свой подмножество.
// ---------------------------------------------------------------------------

export interface PayloadShape {
  surface?: string;
  source_abbreviation?: string;
  source_expansion?: string;
  target_abbreviation?: string;
  target_expansion?: string;
  en_term?: string;
  ru_term?: string;
  ru_term_hints?: Array<string | Record<string, unknown>>;
  source_text?: string;
  candidate_text?: string;
  definition?: string;
  matched_alias?: string;
  matched_text?: string;
  alias?: string;
  alias_kind?: string;
  review_queue?: string;
  review_reason?: string;
  suggested_en_term?: string;
  suggested_actions?: string[];
  domain?: string;
  context_words?: Array<{ word: string; weight: number }>;
  context_words_stems?: string[];
  context_weights?: Record<string, number>;
  examples?: Array<string | Record<string, unknown>>;
  evidence?: Array<string | Record<string, unknown>>;
  disambiguation?: Array<{
    name: string;
    sub?: string;
    score: number;
    is_chosen?: boolean;
  }>;
  source_refs?: string[];
  external_ids?: Record<string, unknown>;
  raw_occurrence_count?: number;
  is_ambiguous?: boolean;
  flags?: string[];
  reviewer_note?: string;
  // Дополнительные поля встречающиеся в окхлп/абревиатурах:
  notes?: string;
  synonyms?: Array<string | Record<string, unknown>>;
  evidence_count?: number;
  source_count?: number;
  active_sources?: string[];
  abbreviation?: string;
  abbreviation_norm?: string;
  definition_norm?: string;
  review_definition?: string;
  competing_senses?: Array<Record<string, unknown>>;
  surface_variants?: string[];
  clinrec_ids?: string[];
  clinrec_titles?: string[];
  mkb_codes?: string[];
  case_matches?: Array<Record<string, unknown>>;
  category?: string;
  // term_alias
  canonical_text?: string;
  canonical_lang?: string;
  alias_text?: string;
  alias_lang?: string;
  approval_policy?: string;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Общие props для всех type-specific форм. Один контракт = легко
// switch'ить по item_type.
// ---------------------------------------------------------------------------

export interface RecordTabFormProps {
  item: ReviewItem;
  payload: PayloadShape;
  editMode: boolean;
  draft: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onCancelEdit: () => void;
  onApplyLlm: (selection: AppliedAlternativeSelection) => void;
}

// ---------------------------------------------------------------------------
// ru_term_hints normalization
// ---------------------------------------------------------------------------

export interface NormalizedHint {
  text: string;
  hintType?: string;
  label?: string;
  caseId?: string;
  weight?: number;
}

/** Backend кладёт RU-варианты в payload.ru_term_hints. Формат разнится:
 *  для enforcement_candidate — массив объектов с ru_term/hint_type/label/case_id;
 *  для других контекстов — массив строк или объектов с term/text/value/translation.
 *  Сводим к единой форме и сортируем preferred → acceptable → по весу. */
export function collectRuHints(
  hints: Array<string | Record<string, unknown>> | undefined,
): NormalizedHint[] {
  if (!hints) return [];
  const out: NormalizedHint[] = [];
  for (const h of hints) {
    if (typeof h === "string") {
      out.push({ text: h });
    } else if (h && typeof h === "object") {
      const obj = h as Record<string, unknown>;
      const text = String(
        obj.ru_term ??
          obj.term ??
          obj.text ??
          obj.value ??
          obj.translation ??
          "",
      );
      if (!text) continue;
      const weight =
        typeof obj.weight === "number"
          ? obj.weight
          : typeof obj.score === "number"
            ? obj.score
            : undefined;
      out.push({
        text,
        hintType: typeof obj.hint_type === "string" ? obj.hint_type : undefined,
        label: typeof obj.label === "string" ? obj.label : undefined,
        caseId: typeof obj.case_id === "string" ? obj.case_id : undefined,
        weight,
      });
    }
  }
  const rank: Record<string, number> = { preferred: 0, acceptable: 1 };
  out.sort((a, b) => {
    const ra = rank[a.hintType ?? ""] ?? 9;
    const rb = rank[b.hintType ?? ""] ?? 9;
    if (ra !== rb) return ra - rb;
    return (b.weight ?? 0) - (a.weight ?? 0);
  });
  return out;
}

// ---------------------------------------------------------------------------
// pickFirst — берёт первое непустое значение из набора кандидатов.
// Используется в RawPayloadFallback (универсальный layout). Type-specific
// формы НЕ должны его использовать — они знают свои поля заранее.
// ---------------------------------------------------------------------------

export function pickFirst(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const c of candidates) {
    if (c != null && c !== "") return c;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// fieldOverridesForItem — зеркало серверной field_overrides_for_item
// (apps/translation_service/llm_prompts.py:field_overrides_for_item).
// При Apply LLM-альтернативы возвращает поля, которые надо подмержить в
// draft. Переиспользуется и в Focus, и в Batch.
// ---------------------------------------------------------------------------

export function fieldOverridesForItem(
  item: ReviewItem,
  targetText: string,
): Record<string, string> {
  const itemType = item.item_type ?? "";
  const targetLang = item.target_lang ?? "";
  if (itemType === "abbreviation") {
    return { target_expansion: targetText, target_text: targetText };
  }
  if (itemType === "term_alias") {
    return { alias_text: targetText, target_text: targetText };
  }
  if (itemType === "enforcement_candidate") {
    return { ru_term: targetText, target_text: targetText };
  }
  if (targetLang === "ru") {
    return { ru_term: targetText, target_text: targetText };
  }
  if (targetLang === "en") {
    return { en_term: targetText, target_text: targetText };
  }
  return { target_text: targetText };
}
